import React, { useState } from 'react';
import { useFormik } from 'formik';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, {
	CardBody,
	CardFooter,
	CardFooterLeft,
	CardFooterRight,
	CardHeader,
	CardLabel,
	CardTitle,
} from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import useDarkMode from '../../../hooks/useDarkMode';
import { demoPagesMenu } from '../../../menu';
import SubHeader, {
	SubHeaderLeft,
	SubHeaderRight,
	SubheaderSeparator,
} from '../../../layout/SubHeader/SubHeader';
import Breadcrumb from '../../../components/bootstrap/Breadcrumb';
import Accordion, { AccordionItem } from '../../../components/bootstrap/Accordion';
import Icon from '../../../components/icon/Icon';
import { toast } from 'react-toastify';
import { useResetPassword } from '../../../apis/hooks/auth';

interface ChangePasswordFormValues {
	validationUserName: string;
}

interface ApiResponse {
	status?: boolean;
	message?: string;
	data?: string;
}

const ResetPassword = () => {
	const { darkModeStatus } = useDarkMode();

	// State to hold the API response message
	const [apiResponse, setApiResponse] = useState<ApiResponse | string | null>(null);
	const [loading, setLoading] = useState<boolean>(false); // Loading state
	const [copySuccess, setCopySuccess] = useState<boolean>(false); // State to manage copy success

	const { mutate: resetPassword } = useResetPassword();

	const formik = useFormik<ChangePasswordFormValues>({
		initialValues: {
			validationUserName: '',
		},
		validate: (values) => {
			const errors: Partial<Record<keyof ChangePasswordFormValues, string>> = {};
			// Validation logic:
			if (!values.validationUserName) {
				// Show "Required" error if field is empty and untouched
				errors.validationUserName = 'Required';
			} else if (!values.validationUserName.trim()) {
				// Show "Name cannot be just spaces" if field contains only spaces
				errors.validationUserName = 'Name cannot be just spaces';
			}
			return errors;
		},
		onSubmit: (values) => {
			const payload = {
				username: values.validationUserName
			};

			// Start the loader before making the API call
			setLoading(true);

			// Call the resetPassword function and set the response in state
			resetPassword(payload, {
				onSuccess: (res: any) => {
					setLoading(false); // Stop the loader after receiving the response
					if (res?.status) {
						toast.success(res?.message, {
							closeOnClick: true,
							closeButton: true,
						});
						// Set success response in the state
						setApiResponse(res);
					} else {
						// Set error response in the state
						setApiResponse(res);
					}
				},
				onError: (err: any) => {
					setLoading(false); // Stop the loader on error
					// Handle error if needed
					setApiResponse('An error occurred, please try again later.');
				},
			});
		},
	});

	// Function to handle copy to clipboard
	const handleCopyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text).then(
			() => {
				setCopySuccess(true); // Show the success state
				toast.success('Copied to clipboard!');
			},
			(err) => {
				setCopySuccess(false); // Show error if copy fails
				toast.error('Failed to copy!');
			}
		);
	};

	return (
		<PageWrapper title={demoPagesMenu.changePassword.text}>
			<SubHeader>
				<SubHeaderLeft>
					<Breadcrumb
						list={[
							{ title: 'Reset Password', to: '/' },
							{
								title: 'Reset Password',
								to: '#',
							},
						]}
					/>
				</SubHeaderLeft>
				<SubHeaderRight>
					<SubheaderSeparator />
					<Button icon='ArrowBackIosNew' color='primary' isLight tag='a' to='/'>
						Go Back
					</Button>
				</SubHeaderRight>
			</SubHeader>

			<Page container='fluid'>
				<Card tag='form' noValidate onSubmit={formik.handleSubmit}>
					<CardHeader>
						<CardLabel>
							<CardTitle>{'Reset Password'}</CardTitle>
						</CardLabel>
					</CardHeader>

					<CardBody>
						{/* Login Info */}
						<Accordion
							id='accSample-shadow-1'
							shadow='default'
							className='mb-5'
							activeItemId='accor1-shadow-1'>
							<AccordionItem id='accor1-shadow-1' title='Reset Password Info' icon='Refresh'>
								<div className='row g-4'>
									<FormGroup
										id='validationUserName'
										label='Email'
										className='col-md-6'>
										<Input {...formik.getFieldProps('validationUserName')} />
										{formik.errors.validationUserName ? (
											<div className='text-danger'>
												{formik.errors.validationUserName}
											</div>
										) : (
											<></>
										)}
									</FormGroup>
								</div>
							</AccordionItem>
						</Accordion>

						{/* Loader while the API request is being processed */}
						{loading && (
							<div className="text-center mt-4">
								<div className="spinner-border text-primary" role="status">
									<span className="sr-only"></span>
								</div>
							</div>
						)}

						{/* Display the API response below the form */}
						{apiResponse && (
							<div className="mt-4">
								{/* Check if the response is an object or string */}
								{typeof apiResponse === 'object' ? (
									apiResponse?.status ? (
										<div className="alert alert-success text-white">
											<div>
												<div className='d-flex'>
													<p><b>{apiResponse?.data}</b></p> {/* Add the Copy to Clipboard button */}
													{/* Use an icon for the "Copy" action */}
													<Icon
														icon='ContentCopy'
														onClick={() => handleCopyToClipboard(apiResponse?.data || '')}
														color="primary"
														aria-label="copy-to-clipboard"
														className="ms-2">
													</Icon>
												</div>
												<br />
												<div>
													<p>{apiResponse?.message}</p>
												</div>
											</div>
										</div>
									) : (
										<div className="alert alert-warning">{apiResponse?.message}</div>
									)
								) : (
									<div className="alert alert-danger">{apiResponse}</div>
								)}
							</div>
						)}
					</CardBody>

					<CardFooter style={{ flexDirection: 'row' }}>
						<CardFooterLeft>
							<Button type='submit' color='primary' className='fs-5'>
								{'Submit'}
							</Button>
						</CardFooterLeft>
						<CardFooterRight>
							<Button
								type='reset'
								color='info'
								isOutline
								onClick={formik.handleReset}
								className='fs-5'>
								Reset
							</Button>
						</CardFooterRight>
					</CardFooter>
				</Card>
			</Page>
		</PageWrapper>
	);
};

export default ResetPassword;
